import Box from '@components/base/Box'
import { Button } from '@components/inputs/Button'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { TextAreaInput } from '@components/inputs/TextAreaInput'
import { TextInput } from '@components/inputs/TextInput'
import Modal from '@components/base/Modal'
import { EventStageInput, StageType } from '@graphql/__generated/graphql'
import CreateEventLayout from '@layouts/CreateEventLayout'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import React, { useState } from 'react'
import Table from '@components/base/Table'
import TableRowActions from '@components/event/TableRowActions'
import useAutosave from '@libs/useAutosave'
import { Form } from '@components/inputs/Form'

const StagesForm = () => {
  const [showStageModal, setShowStageModal] = useState(false)
  const [modalData, setModalData] = useState<any>()
  const { formData, setFormData, errors, setErrors } =
    useEventEditor()
  const { stages } = formData ?? {}
  const noStages = stages == undefined || stages.length === 0
  const { setHasEdited } = useAutosave()
  const [stageModalData, setStageModalData] = useState<
    Partial<EventStageInput & { editIndex?: number }> | undefined
  >(undefined)

  const errorHandler =
    (id: string, alert: boolean = false) =>
    (message: string, testId: string) => {
      const payload = [...errors]

      const hasError = !!message || message !== ''
      const isCleared = payload.find((f) => f.eleId === testId) && !hasError

      if (isCleared) return setErrors(payload.filter((f) => f.eleId !== testId))

      const isDuplicate = !!payload.find((p) => p.eleId === testId)

      const newError = {
        step: id ?? 'unknown',
        message,
        eleId: testId,
        alert,
      }

      if (!isDuplicate && hasError) setErrors([...payload, newError])
    }

  const addStageModal = (
    <Modal
      show={showStageModal}
      setShow={(visible) => {
        if (!visible) {
          setStageModalData(undefined)
        }
        setShowStageModal(visible)
      }}
      title={`${
        modalData?.editIndex !== undefined ? 'Update' : 'Create'
      } Stage`}
    >
      <Form
        initialFormData={{
          title: stageModalData?.title ?? '',
          description: stageModalData?.description ?? '',
          holdingVideoUrl: stageModalData?.holdingVideoUrl ?? '',
          stageType: StageType.Internal,
          editIndex: stageModalData?.editIndex ?? '',
        }}
        onSubmit={async (data) => {
          const { title, description, holdingVideoUrl, stageType, editIndex } =
            data

          let payload = {
            ...formData,
            stages: [...(stages ?? [])],
          }
          const newStage = {
            title: title ?? '',
            class: stageType,
            ...(description && {
              description: description,
            }),
            ...(holdingVideoUrl && {
              holdingVideoUrl: holdingVideoUrl,
            }),
          }

          if (editIndex || editIndex === 0) payload.stages[editIndex] = newStage
          else payload.stages.push(newStage)

          setFormData(payload)
          setStageModalData(undefined)
          setHasEdited(true)
          setShowStageModal(false)
        }}
      >
        <TextInput
          required
          label="Title"
          placeholder="Stage Name"
          value={stageModalData?.title}
          setError={errorHandler('stages')}
          // onChange={(data) => {
          //   setStageModalData({ ...stageModalData, title: data })
          // }}
        />
        <TextAreaInput
          className="mt-8"
          label="Description"
          placeholder="Write your description"
          value={stageModalData?.description}
          setError={errorHandler('stages')}
          // onChange={(data) => {
          //   setStageModalData({ ...stageModalData, description: data })
          // }}
        />
        <DropdownInput
          label="Stage Type"
          required
          value={stageModalData?.class}
          onChange={(data) => {
            setStageModalData({ ...stageModalData, class: data })
          }}
          options={[
            { label: 'Hosted by 3rd Party', value: StageType.External },
            { label: 'Hosted on Forumm', value: StageType.Internal },
          ]}
          placeholder="Select stage type"
        />
        <TextInput
          validations={{
            isURL: true,
          }}
          label={
            stageModalData?.class === StageType.External
              ? 'Live Video Url'
              : 'Holding Video Url'
          }
          placeholder="https://www.youtube.com/..."
          value={stageModalData?.holdingVideoUrl}
          setError={errorHandler('stages')}
          // onChange={(data) => {
          //   setStageModalData({ ...stageModalData, holdingVideoUrl: data })
          // }}
        />
        <Button
          className="my-4"
          title={`${
            stageModalData?.editIndex !== undefined ? 'Update' : 'Create'
          } Stage`}
          type="primary"
          buttonType="submit"
        />
      </Form>
    </Modal>
  )

  return (
    <>
      {addStageModal}
      <Box className="w-full">
        <Box className="mt-6 mb-4 flex">
          <Button
            onClick={() => {
              setShowStageModal(true)
              setModalData(undefined)
            }}
            title="Add Stage"
            type="secondary"
            size="small"
          />
        </Box>
        <Table
          tableHeading={['Title', 'Description', 'Holding Video', '']}
          rows={stages?.map((stage, index) => [
            stage.title,
            stage.description ?? '',
            stage.holdingVideoUrl ?? '',
            <TableRowActions
              key={index}
              confirmModal={{
                title: 'Are you sure?',
                content:
                  'Are you sure you want to delete this? This cannot be undone.',
              }}
              editClicked={() => {
                setStageModalData({
                  title: stage.title,
                  editIndex: index,
                  class: stage.class,
                  ...(stage.description && { description: stage.description }),
                  ...(stage.holdingVideoUrl && {
                    holdingVideoUrl: stage.holdingVideoUrl,
                  }),
                })
                setShowStageModal(true)
              }}
              deleteClicked={() => {
                setHasEdited(true)
                setFormData({
                  ...formData,
                  stages: stages.filter((s) => s.title !== stage.title),
                })
              }}
            />,
          ])}
        />
        {noStages && (
          <Box>
            <Box className="text-sm mt-8 text-center">
              {'No stages have been created'}
            </Box>
            <hr className="my-6 border-gray-600 opacity-50" />
          </Box>
        )}
      </Box>
    </>
  )
}

StagesForm.Layout = CreateEventLayout

export default StagesForm
